<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { useProgramEnrollments } from '@/composables/eos/program-enrollments'
import { formatTimeWindow } from '@/helpers/eos/table-formatters'
import type { IOptOutPeriodFilters, IProgramEnrollment } from '@/types/eos/ProgramEnrollment'
import { Delete } from '@element-plus/icons-vue'
import { computed, reactive, ref, watch } from 'vue'

const OPT_OUT_PERIODS_PAGE_SIZE = 5

const props = defineProps<{
  enrollment: IProgramEnrollment
  disabled: boolean
  timezone?: string
  /** Set once a period has been created for this enrollment elsewhere (the "add" form lives
   * in the parent) — the embedded first page on `enrollment` is a snapshot from whenever the
   * enrollments list last loaded, so from that point on we trust the dedicated endpoint instead. */
  forceRefresh?: boolean
}>()

const emit = defineEmits<{
  'delete-opt-out-period': [
    payload: {
      programEnrollmentId: number
      optOutPeriodId: number
      onSuccess: () => void
      onError: (err: unknown) => void
    },
  ]
}>()

const { optOutPeriodsQuery } = useProgramEnrollments()

// The enrollment payload already embeds the first page — the dedicated paginated query only
// kicks in once the user asks for another page, or a create/delete makes that snapshot stale.
const hasRequestedPage = ref(false)
const hasDeleted = ref(false)
const usingDedicatedEndpoint = computed(() => hasRequestedPage.value || hasDeleted.value || !!props.forceRefresh)
const filters = reactive<IOptOutPeriodFilters>({ page: 1, per_page: OPT_OUT_PERIODS_PAGE_SIZE })

const { data: fetchedPage, isFetching } = optOutPeriodsQuery(props.enrollment.id, filters, usingDedicatedEndpoint)

const page = computed(() =>
  usingDedicatedEndpoint.value && fetchedPage.value ? fetchedPage.value : props.enrollment.opt_out_periods
)

// A delete can leave `filters.page` pointing past the last page (e.g. deleting the
// only item on the current page) — pull it back in range so the list doesn't strand
// the user on an empty page with no visible way back.
watch(fetchedPage, (newPage) => {
  if (newPage && filters.page > newPage.total_pages) {
    filters.page = newPage.total_pages
  }
})

function onPageChange(newPage: number) {
  filters.page = newPage
  hasRequestedPage.value = true
}

function deleteOptOutPeriod(optOutPeriodId: number) {
  return new Promise<void>((resolve, reject) => {
    emit('delete-opt-out-period', {
      programEnrollmentId: props.enrollment.id,
      optOutPeriodId,
      onSuccess: () => {
        hasDeleted.value = true
        resolve()
      },
      onError: reject,
    })
  })
}

function confirmDeleteOptOutPeriod(optOutPeriodId: number) {
  DeleteConfirmationModal(
    'Delete Opt-Out Period',
    'Are you sure you want to delete this opt-out period? This cannot be undone.',
    optOutPeriodId,
    deleteOptOutPeriod
  )
}

// Exposed so the parent's "add opt-out period" form can validate the new range against the
// periods already loaded here — only a fraction of all existing periods, but still a
// worthwhile real-time check on top of the BE's own overlap validation.
defineExpose({
  get loadedPeriods() {
    return page.value.data
  },
})
</script>

<template>
  <div v-loading="isFetching" class="flex flex-col gap-2">
    <el-pagination
      v-if="page.total_records > OPT_OUT_PERIODS_PAGE_SIZE"
      small
      layout="prev, pager, next"
      :current-page="filters.page"
      :page-size="OPT_OUT_PERIODS_PAGE_SIZE"
      :page-count="page.total_pages"
      :disabled="disabled || isFetching"
      class="self-end"
      @current-change="onPageChange"
    />

    <div v-if="page.data.length" class="flex flex-col gap-2">
      <div v-for="period in page.data" :key="period.id" class="flex items-center justify-between text-sm">
        <span class="text-text-secondary">{{
          formatTimeWindow(period.opt_out_start_utc, period.opt_out_end_utc, timezone)
        }}</span>
        <el-button link :icon="Delete" :disabled="disabled" @click="confirmDeleteOptOutPeriod(period.id)" />
      </div>
    </div>
    <span v-else class="text-xs text-text-tertiary">No opt-out periods</span>
  </div>
</template>
